/** Centralized document type definitions for the Jerry legal workbench. */

import type { RagIndexStatus } from "../types";

// ---------------------------------------------------------------------------
// AI retrieval index status (document Q&A readiness)
// ---------------------------------------------------------------------------

/**
 * Named values for the document AI index status. This const is the single
 * source of the status values — the `RagIndexStatus` union in ../types is
 * DERIVED from it, so adding a status here automatically extends the type
 * (and the labels record below then fails to compile until updated).
 */
export const RAG_INDEX_STATUS = {
  PENDING: "pending",
  READY: "ready",
  FAILED: "failed",
  DEGRADED: "degraded",
} as const;

/** Short Chinese label shown as the per-document AI index indicator. */
export const RAG_INDEX_STATUS_LABELS: Record<RagIndexStatus, string> = {
  pending: "索引中",
  ready: "可问答",
  failed: "索引失败",
  degraded: "索引失效",
};

/** Toast shown when a file was saved but its AI index build failed. */
export const RAG_INDEX_FAILED_NOTICE =
  "文档已保存，但 AI 索引失败，暂不能对其内容问答";

// ---------------------------------------------------------------------------
// Conversation attachments (in-chat document upload for Q&A)
// ---------------------------------------------------------------------------

/** File extensions accepted as chat attachments (supported for document Q&A). */
export const CHAT_ATTACHMENT_EXTENSIONS = [
  "pdf", "docx", "doc", "txt", "md", "markdown",
  "jpg", "jpeg", "png", "webp",
  "xlsx", "pptx",
] as const;

/** `accept` attribute for the chat attachment file input. */
export const CHAT_ATTACHMENT_ACCEPT =
  ".pdf,.docx,.doc,.txt,.md,.markdown,.jpg,.jpeg,.png,.webp,.xlsx,.pptx";

/** Error codes returned by the conversation attachment / send-message endpoints. */
export const CHAT_ATTACHMENT_ERROR = {
  /** Uploaded file type is not supported for document Q&A. */
  UNSUPPORTED_TYPE: "CHAT_ATTACHMENT_UNSUPPORTED_TYPE",
  /** One or more attachments are still indexing; the send must be retried. */
  DOCUMENTS_NOT_READY: "CHAT_DOCUMENTS_NOT_READY",
} as const;

/** Rejection shown for any unsupported extension. */
export const CHAT_ATTACHMENT_UNSUPPORTED_NOTICE =
  "支持 PDF / Word / Excel / PPT / Markdown / 图片 / 文本";

/**
 * Decide whether a selected file is eligible as a chat attachment, by extension.
 * Returns null when acceptable; otherwise the rejection notice to show.
 * Rejected files are never uploaded.
 */
export function chatAttachmentRejection(filename: string): string | null {
  const ext = filename.toLowerCase().split(".").pop() ?? "";
  const hasExtension = filename.includes(".");
  if (
    hasExtension &&
    (CHAT_ATTACHMENT_EXTENSIONS as readonly string[]).includes(ext)
  ) {
    return null;
  }
  return CHAT_ATTACHMENT_UNSUPPORTED_NOTICE;
}

/** Join document titles as 《title》、《title》 for a notice sentence. */
function joinDocumentTitles(docs: { title: string }[]): string {
  return docs.map((d) => `《${d.title}》`).join("、");
}

/**
 * Notice shown when a send is rejected because one or more attachments are still
 * being indexed (CHAT_DOCUMENTS_NOT_READY). Multi-document aware.
 */
export function buildDocumentsNotReadyNotice(
  docs: { title: string }[],
): string {
  return `${joinDocumentTitles(docs)}仍在处理中，请稍后重试`;
}

/**
 * Notice shown when a reply came back but some attachments were excluded from
 * its retrieval scope because their index build failed. Multi-document aware.
 */
export function buildSkippedDocumentsNotice(
  docs: { title: string }[],
): string {
  return `${joinDocumentTitles(docs)}索引失败，未纳入本次回答范围`;
}

// ---------------------------------------------------------------------------
// Document type literal union
// ---------------------------------------------------------------------------

/** Legal document type classification. */
export type DocumentType =
  | "contract"
  | "legal_opinion"
  | "memo"
  | "pleading"
  | "evidence"
  | "due_diligence_report"
  | "correspondence"
  | "court_document"
  | "meeting_minutes"
  | "template"
  | "other";

// ---------------------------------------------------------------------------
// Document type -> Chinese label
// ---------------------------------------------------------------------------

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  contract: "合同",
  legal_opinion: "法律意见书",
  memo: "备忘录",
  pleading: "诉状",
  evidence: "证据",
  due_diligence_report: "尽职调查报告",
  correspondence: "函件",
  court_document: "法院文书",
  meeting_minutes: "会议纪要",
  template: "模板",
  other: "其他",
};

// ---------------------------------------------------------------------------
// Document type -> select options for forms
// ---------------------------------------------------------------------------

export const DOCUMENT_TYPE_OPTIONS: {
  value: DocumentType;
  label: string;
}[] = [
  { value: "contract", label: "合同" },
  { value: "legal_opinion", label: "法律意见书" },
  { value: "memo", label: "备忘录" },
  { value: "pleading", label: "诉状" },
  { value: "evidence", label: "证据" },
  { value: "due_diligence_report", label: "尽职调查报告" },
  { value: "correspondence", label: "函件" },
  { value: "court_document", label: "法院文书" },
  { value: "meeting_minutes", label: "会议纪要" },
  { value: "template", label: "模板" },
  { value: "other", label: "其他" },
];

// ---------------------------------------------------------------------------
// Document type -> folder label (for tree view)
// ---------------------------------------------------------------------------

export const DOCUMENT_TYPE_FOLDER_LABELS: Record<string, string> = {
  contract: "合同文件",
  legal_opinion: "法律意见",
  memo: "备忘录",
  pleading: "诉状",
  evidence: "证据",
  due_diligence_report: "尽职调查报告",
  correspondence: "函件",
  court_document: "法院文书",
  meeting_minutes: "会议纪要",
  template: "模板",
  other: "其他",
};

// ---------------------------------------------------------------------------
// Document type -> icon (emoji for tree view)
// ---------------------------------------------------------------------------

export const DOCUMENT_TYPE_ICONS: Record<string, string> = {
  contract: "contract",
  legal_opinion: "legal_opinion",
  memo: "memo",
  pleading: "pleading",
  evidence: "evidence",
  due_diligence_report: "due_diligence_report",
  correspondence: "correspondence",
  court_document: "court_document",
  meeting_minutes: "meeting_minutes",
  template: "template",
  other: "other",
};

// ---------------------------------------------------------------------------
// Document type -> short tag color class key (for visual differentiation)
// ---------------------------------------------------------------------------

export const DOCUMENT_TYPE_TAG_COLORS: Record<string, string> = {
  contract: "primary",
  legal_opinion: "ink",
  memo: "success",
  pleading: "danger",
  evidence: "purple",
  due_diligence_report: "info",
  correspondence: "warning",
  court_document: "danger",
  meeting_minutes: "info",
  template: "tertiary",
  other: "tertiary",
};

// ---------------------------------------------------------------------------
// Document source type (aligned with backend DocSource enum)
// ---------------------------------------------------------------------------

/** Document origin classification. */
export type DocSource =
  | "client_upload"
  | "team_upload"
  | "ai_generated"
  | "opposing_party"
  | "court"
  | "external";

export const DOC_SOURCE_LABELS: Record<DocSource, string> = {
  client_upload: "客户上传",
  team_upload: "团队上传",
  ai_generated: "AI生成",
  opposing_party: "对方当事人",
  court: "法院",
  external: "外部来源",
};
